import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Calendar Settings Store
 *
 * Manages user preferences for calendar view:
 * - View mode (month/week/day)
 * - Time format (blocks vs hourly)
 * - Moon phases display
 * - Sidebar state
 */

export type CalendarView = 'month' | 'week' | 'day';
export type TimeFormat = 'blocks' | 'hourly';

export interface CalendarSettings {
  // Display preferences
  timeFormat: TimeFormat;
  showMoonPhases: boolean;
  defaultView: CalendarView;
  weekStartsOn: 0 | 1; // 0 = Sunday, 1 = Monday

  // UI state
  sidebarOpen: boolean;
}

interface CalendarSettingsStore extends CalendarSettings {
  // Actions
  updateSettings: (settings: Partial<CalendarSettings>) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  reset: () => void;
}

const DEFAULT_SETTINGS: CalendarSettings = {
  timeFormat: 'blocks',
  showMoonPhases: false,
  defaultView: 'month',
  weekStartsOn: 1, // Monday
  sidebarOpen: true,
};

export const useCalendarSettingsStore = create<CalendarSettingsStore>()(
  persist(
    (set) => ({
      ...DEFAULT_SETTINGS,

      updateSettings: (settings: Partial<CalendarSettings>) =>
        set((state) => ({ ...state, ...settings })),

      toggleSidebar: () =>
        set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      setSidebarOpen: (open: boolean) =>
        set({ sidebarOpen: open }),

      reset: () => set(DEFAULT_SETTINGS),
    }),
    {
      name: 'calendar-settings',
    }
  )
);
